import {
    BMBreakdownOptionEnum,
    LeaderboardMarketPriceOptionEnum,
    LeaderboardOptionTypeEnum,
    NonDeliverySplitOutOptionEnum,
} from '@/enums/powerMarkets/LeaderboardEnums.ts';
import {
    createDefaultLeaderboardDateModel,
    DEFAULT_LEADERBOARD_MODAL_OPTIONS,
    DEFAULT_LEADERBOARD_SETTINGS,
    type ILeaderboardModalOptions,
    type ILeaderboardUserOptions,
} from '@/interfaces/powerMarkets/settings/pages/ILeaderboardSettings.ts';
import { type ILeaderboardGroup, ILeaderboardPreset } from '@/interfaces/powerMarkets/leaderboard/ILeaderboardGroup.ts';
import { LeaderboardPoolingModeEnum } from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import { getFuelsForLeaderboardType } from '@/helpers/powerMarkets/leaderboardHelpers.ts';
import { cloneDeep } from '@/utils/global.ts';

export const DEFAULT_LEADERBOARD_ICON = 'list';
export const DEFAULT_LEADERBOARD_ICON_COLOUR = 'var(--lcpBlue)';

function createComparisonOptionsFromUserOptions (userOptions: ILeaderboardUserOptions): ILeaderboardModalOptions {
    return (Object.keys(DEFAULT_LEADERBOARD_MODAL_OPTIONS) as Array<keyof ILeaderboardModalOptions>).reduce(
        (acc, key) => ({ ...acc, [key]: userOptions[key] }),
        {} as ILeaderboardModalOptions,
    );
}

const BESS_ADVANCED_USER_OPTIONS: ILeaderboardUserOptions = {
    ...DEFAULT_LEADERBOARD_SETTINGS.userOptions,
    leaderboardType: LeaderboardOptionTypeEnum.Plant,
    revenueType: 'PoundPerKwPerYear',
    fuels: ['Battery'],
    ownerFuels: [],
    selectedDateModel: createDefaultLeaderboardDateModel(),
    includeWholesaleSplit: true,
    bmProfitOptions: [BMBreakdownOptionEnum.BidOfferSplit, BMBreakdownOptionEnum.SystemEnergySplit],
};

export function createDefaultLeaderboardGroup (): ILeaderboardGroup {
    return {
        id: crypto.randomUUID(),
        name: '',
        description: '',
        icon: DEFAULT_LEADERBOARD_ICON,
        iconColour: DEFAULT_LEADERBOARD_ICON_COLOUR,
        userOptions: cloneDeep(DEFAULT_LEADERBOARD_SETTINGS.userOptions),
        poolingMode: LeaderboardPoolingModeEnum.NonPooled,
    };
}

export function createLeaderboardGroupFromPreset (preset: ILeaderboardPreset): ILeaderboardGroup {
    const userOptions = cloneDeep(preset.userOptions);
    if (userOptions.leaderboardType == null) userOptions.leaderboardType = LeaderboardOptionTypeEnum.Plant;

    const group: ILeaderboardGroup = {
        id: crypto.randomUUID(),
        name: preset.name,
        description: preset.description,
        icon: preset.icon,
        iconColour: preset.iconColour,
        userOptions,
        poolingMode: LeaderboardPoolingModeEnum.NonPooled,
    };

    if (preset.initialTableFilters) {
        group.initialTableFilters = cloneDeep(preset.initialTableFilters);
    }
    if (getFuelsForLeaderboardType(userOptions).includes('Battery')) {
        userOptions.includeBessInfo = true;
    }

    return group;
}

export const LEADERBOARD_PRESETS: Array<ILeaderboardPreset> = [
    {
        name: 'BESS',
        description: 'A starting plant leaderboard for BESS assets',
        icon: 'battery-half',
        iconColour: 'var(--lcpSunrise)',
        userOptions: {
            ...DEFAULT_LEADERBOARD_SETTINGS.userOptions,
            leaderboardType: LeaderboardOptionTypeEnum.Plant,
            revenueType: 'PoundPerKwPerYear',
            fuels: ['Battery'],
            ownerFuels: [],
            selectedDateModel: createDefaultLeaderboardDateModel(),
            groupDxProducts: true,
            nonDeliverySplitOut: NonDeliverySplitOutOptionEnum.Absorb,
            reservePenaltySplitOut: 'Absorb',
        },
    },
    {
        name: 'BESS Advanced',
        description: 'A starting plant leaderboard for BESS assets, with more explicit breakdowns',
        icon: 'battery-half',
        iconColour: 'var(--lcpWatermelon)',
        userOptions: {
            ...BESS_ADVANCED_USER_OPTIONS,
            comparisonOptions: {
                ...createComparisonOptionsFromUserOptions(BESS_ADVANCED_USER_OPTIONS),
                marketPrice: LeaderboardMarketPriceOptionEnum.IntradayPrice,
            },
        },
    },
    {
        name: 'CCGT',
        description: 'A starting plant leaderboard for CCGT assets',
        icon: 'industries-1',
        iconColour: 'var(--lcpBlue)',
        userOptions: {
            ...DEFAULT_LEADERBOARD_SETTINGS.userOptions,
            leaderboardType: LeaderboardOptionTypeEnum.Plant,
            revenueType: 'Pound',
            fuels: ['CCGT'],
            ownerFuels: [],
            selectedDateModel: createDefaultLeaderboardDateModel(),
        },
    },
    {
        name: 'Gas',
        description: 'A starting plant leaderboard for all gas assets',
        icon: 'industries-1',
        iconColour: 'var(--lcpBlue)',
        userOptions: {
            ...DEFAULT_LEADERBOARD_SETTINGS.userOptions,
            leaderboardType: LeaderboardOptionTypeEnum.Plant,
            revenueType: 'Pound',
            fuels: ['CCGT', 'OCGT', 'GasRecips'],
            ownerFuels: [],
            selectedDateModel: createDefaultLeaderboardDateModel(),
        },
    },
];
